import { useEffect, useMemo } from "react";
import type { LucideIcon } from "lucide-react";

import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandShortcut,
} from "@/components/ui/command";

export interface CommandAction {
  id: string;
  label: string;
  group: string;
  icon?: LucideIcon;
  shortcut?: string;
  // Eine Zeile neben dem Titel (NAV-4: «Anfragen seit Wochen ohne Bewegung»).
  // Erklaert, benennt nicht — der Titel bleibt das Seiten-Wort.
  description?: string;
  keywords?: readonly string[];
  onSelect: () => void;
}

export interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  actions: readonly CommandAction[];
  // Die Eingabe gehoert dem Provider (SUCHE-1): Server-Suchen lesen sie mit.
  search: string;
  onSearchChange: (search: string) => void;
  placeholder?: string;
  emptyLabel?: string;
}

export function CommandPalette({
  open,
  onOpenChange,
  actions,
  search,
  onSearchChange,
  placeholder = "Suchen oder Aktion ausführen...",
  emptyLabel = "Keine Treffer.",
}: CommandPaletteProps) {
  const grouped = useMemo(() => {
    const map = new Map<string, CommandAction[]>();
    for (const action of actions) {
      const list = map.get(action.group);
      if (list) {
        list.push(action);
      } else {
        map.set(action.group, [action]);
      }
    }
    return Array.from(map.entries());
  }, [actions]);

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput
        placeholder={placeholder}
        value={search}
        onValueChange={onSearchChange}
      />
      <CommandList>
        <CommandEmpty>{emptyLabel}</CommandEmpty>
        {/* Trennlinie als Rahmen der Gruppe, nicht als eigenes Element: ein
            role="separator" ist im listbox kein erlaubtes Kind (Axe
            aria-required-children, NAV-4). */}
        {grouped.map(([group, items], idx) => (
          <CommandGroup
            key={group}
            heading={group}
            className={idx > 0 ? "border-t border-border" : undefined}
          >
            {items.map((action) => {
              const Icon = action.icon;
              return (
                <CommandItem
                  key={action.id}
                  value={`${action.label} ${(action.keywords ?? []).join(" ")}`}
                  onSelect={() => {
                    onOpenChange(false);
                    action.onSelect();
                  }}
                >
                  {Icon ? <Icon className="size-4 shrink-0 text-muted-foreground" /> : null}
                  <span className="truncate" data-palette-label="">
                    {action.label}
                  </span>
                  {action.description ? (
                    <span
                      className="min-w-0 truncate text-xs text-muted-foreground"
                      data-palette-description=""
                    >
                      {action.description}
                    </span>
                  ) : null}
                  {action.shortcut ? (
                    <CommandShortcut>{action.shortcut}</CommandShortcut>
                  ) : null}
                </CommandItem>
              );
            })}
          </CommandGroup>
        ))}
      </CommandList>
    </CommandDialog>
  );
}

export function useCommandPaletteHotkey(toggle: () => void): void {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        toggle();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [toggle]);
}
